import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../assets/styles/demochatwidget.css";

const getApiBaseUrl = () => {
  const configured =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    "https://backend.imergey.com/api";
  return configured.replace(/\/+$/, "");
};

const AGENT_NAME = "Julie";
const INTEREST_OPTIONS = ["Content & scheduling", "Brand management", "Analytics & reporting", "Show me everything"];

const AUTO_OPEN_DISMISS_KEY = "imergey_widget_auto_open_dismissed"; // sessionStorage — don't re-auto-open after they close it once
const BOTTOM_NUDGE_KEY = "imergey_widget_bottom_nudge_done"; // sessionStorage — the "reached the bottom" nudge only ever fires once per visit
const BOOKED_KEY = "imergey_demo_booked"; // localStorage — sticks forever once they've actually booked
const PROMO_DUE_AT_KEY = "imergey_widget_promo_due_at"; // sessionStorage — epoch ms; survives a refresh so the countdown isn't lost

const SCROLL_TRIGGER_PX = 150; // auto-open as soon as they show intent by scrolling
const AUTO_OPEN_TIMEOUT_MS = 2500; // ...or after this long if they never scroll
const PROMO_AFTER_CLOSE_MS = 5000; // center popup fires this long after they close the panel unengaged
const PROMO_REPEAT_MS = 15000; // and keeps gently resurfacing until they engage or book
const BOTTOM_THRESHOLD_PX = 250; // how close to the page bottom counts as "reached the end"

const firstNameOf = (full) => (full || "").trim().split(/\s+/)[0] || "there";
const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

const upcomingWeekdays = (count = 6) => {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < count) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) {
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      days.push({ label: d.toLocaleDateString("en-GB", { weekday: "short" }), sub: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }), value });
    }
    d.setDate(d.getDate() + 1);
  }
  return days;
};

const formatSlotLabel = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const formatFull = (iso) => new Date(iso).toLocaleString([], { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" });

let wMsgId = 0;

export default function DemoChatWidget() {
  // visibility: 'bubble' | 'panel' — the bubble icon is present from the
  // moment the page loads; the panel auto-opens itself (see effect below).
  const [mode, setMode] = useState("bubble");
  const [closingPanel, setClosingPanel] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState("boot");
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [slots, setSlots] = useState(null);
  const [answers, setAnswers] = useState({});
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const bootedRef = useRef(false);
  const autoOpenedRef = useRef(false);
  // Only a real reply (their name) counts as engagement — auto-opening the
  // panel on its own doesn't stop the promo popup from later showing up.
  const engagedRef = useRef(false);
  const bookedRef = useRef(typeof window !== "undefined" && localStorage.getItem(BOOKED_KEY) === "1");
  const promoTimerRef = useRef(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const days = useMemo(() => upcomingWeekdays(6), []);
  const streamRef = useRef(null);
  const inputRef = useRef(null);

  // Stores the "show again at" timestamp in sessionStorage so a page refresh
  // resumes the same countdown instead of losing it (or silently going quiet).
  const schedulePromo = (delay) => {
    clearTimeout(promoTimerRef.current);
    if (bookedRef.current) { sessionStorage.removeItem(PROMO_DUE_AT_KEY); return; }
    sessionStorage.setItem(PROMO_DUE_AT_KEY, String(Date.now() + delay));
    promoTimerRef.current = setTimeout(() => {
      if (engagedRef.current || bookedRef.current) return;
      setShowPromo(true);
      setMode("bubble");
    }, delay);
  };

  const cancelPromo = () => {
    clearTimeout(promoTimerRef.current);
    sessionStorage.removeItem(PROMO_DUE_AT_KEY);
  };

  const bootGreeting = () => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    agentSay([`Hi! I'm ${AGENT_NAME} 👋`, "Let's get your demo booked — what's your name?"], "name");
  };

  // Auto-open the full panel — on the first sign of scroll intent, or after
  // a short timeout if they never scroll. Whichever fires first wins; only
  // fires once, and never again if they've already closed it or booked.
  useEffect(() => {
    if (sessionStorage.getItem(AUTO_OPEN_DISMISS_KEY) || bookedRef.current) return;

    const triggerAutoOpen = () => {
      if (autoOpenedRef.current) return;
      autoOpenedRef.current = true;
      setMode("panel");
      bootGreeting();
    };

    const onScroll = () => { if (window.scrollY > SCROLL_TRIGGER_PX) triggerAutoOpen(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    const fallbackTimer = setTimeout(triggerAutoOpen, AUTO_OPEN_TIMEOUT_MS);

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(fallbackTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reached the bottom of the page — a strong "read everything, still here"
  // signal. Re-surface the chat with a tailored line instead of the panel's
  // generic opener. Fires at most once per visit, and never once they've
  // actually engaged or booked.
  useEffect(() => {
    const onScrollBottom = () => {
      if (engagedRef.current || bookedRef.current) return;
      if (sessionStorage.getItem(BOTTOM_NUDGE_KEY)) return;
      if (modeRef.current === "panel") return;
      const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - BOTTOM_THRESHOLD_PX;
      if (!nearBottom) return;

      sessionStorage.setItem(BOTTOM_NUDGE_KEY, "1");
      cancelPromo();
      setShowPromo(false);
      autoOpenedRef.current = true;
      setMode("panel");
      if (!bootedRef.current) {
        bootedRef.current = true;
        agentSay(
          [`Hi! I'm ${AGENT_NAME} 👋`, "Looks like you've read through everything — want to grab a free demo? What's your name?"],
          "name"
        );
      } else {
        agentSay(["Made it to the end 👋 — still curious? I'm right here whenever you're ready to grab a time."]);
      }
    };
    window.addEventListener("scroll", onScrollBottom, { passive: true });
    return () => window.removeEventListener("scroll", onScrollBottom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resume a pending promo countdown across a page refresh. If the due time
  // already passed while the page was reloading, show it right away instead
  // of silently dropping it.
  useEffect(() => {
    if (bookedRef.current) { sessionStorage.removeItem(PROMO_DUE_AT_KEY); return; }
    const dueAtRaw = sessionStorage.getItem(PROMO_DUE_AT_KEY);
    if (!dueAtRaw) return;
    const remaining = Number(dueAtRaw) - Date.now();
    schedulePromo(Math.max(0, remaining));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimeout(promoTimerRef.current), []);

  const scrollDown = () => requestAnimationFrame(() => { if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight; });
  const pushUser = (text) => { setMessages((m) => [...m, { id: ++wMsgId, from: "user", text }]); scrollDown(); };

  const agentSay = (texts, nextStep, extra = {}) => {
    const list = Array.isArray(texts) ? texts : [texts];
    let delay = 300;
    list.forEach((text, i) => {
      setTimeout(() => { setTyping(true); scrollDown(); }, delay);
      const typeTime = Math.min(500 + text.length * 12, 1400);
      delay += typeTime;
      const isLast = i === list.length - 1;
      setTimeout(() => {
        setTyping(false);
        setMessages((m) => [...m, { id: ++wMsgId, from: "agent", text, ...(isLast ? extra : {}) }]);
        if (isLast && nextStep) setStep(nextStep);
        scrollDown();
      }, delay);
      delay += 200;
    });
  };

  const openPanel = () => {
    autoOpenedRef.current = true;
    cancelPromo();
    setShowPromo(false);
    setMode("panel");
    bootGreeting();
  };

  const dismissPromo = () => {
    setShowPromo(false);
    schedulePromo(PROMO_REPEAT_MS);
  };

  // Closing the panel without ever replying is exactly the "ignored it"
  // signal — that's what schedules the bigger center popup. The panel plays
  // its genie-close animation first, then actually unmounts once it's done.
  const minimizePanel = () => {
    if (closingPanel) return;
    setClosingPanel(true);
    sessionStorage.setItem(AUTO_OPEN_DISMISS_KEY, "1");
    if (!engagedRef.current) schedulePromo(PROMO_AFTER_CLOSE_MS);
    setTimeout(() => {
      setMode("bubble");
      setClosingPanel(false);
    }, 300);
  };

  useEffect(() => { if (mode === "panel" && ["name", "email", "company"].includes(step)) inputRef.current?.focus(); }, [mode, step]);

  const submitText = () => {
    const value = inputValue.trim();
    if (step === "name") {
      if (!value) return setInputError("A name would help 🙂");
      // First real reply — they're genuinely engaging, stop the promo nudges for good this visit.
      engagedRef.current = true;
      cancelPromo();
      setShowPromo(false);
      setInputError(""); setInputValue(""); pushUser(value);
      setAnswers((a) => ({ ...a, name: value }));
      agentSay([`Nice to meet you, ${firstNameOf(value)}! What's your email?`], "email");
    } else if (step === "email") {
      if (!isValidEmail(value)) return setInputError("That email doesn't look quite right");
      setInputError(""); setInputValue(""); pushUser(value);
      setAnswers((a) => ({ ...a, email: value }));
      agentSay(["Company name? (optional — hit send to skip)"], "company");
    } else if (step === "company") {
      setInputError(""); setInputValue("");
      pushUser(value || "(skipped)");
      setAnswers((a) => ({ ...a, company: value }));
      agentSay(["What should we focus on?"], "interest", { chips: INTEREST_OPTIONS });
    }
  };

  const pickInterest = (opt) => {
    pushUser(opt); setAnswers((a) => ({ ...a, interest: opt })); setStep("waiting");
    agentSay(["Pick a day that works:"], "date", { days: true });
  };

  const pickDate = async (day) => {
    pushUser(`${day.label}, ${day.sub}`); setAnswers((a) => ({ ...a, date: day.value })); setStep("waiting");
    setTyping(true); scrollDown();
    try {
      const res = await fetch(`${getApiBaseUrl()}/user/demo-booking/slots?date=${day.value}`);
      const json = await res.json();
      const available = (json?.data?.slots || []).filter((s) => s.available);
      setTyping(false);
      if (!available.length) { agentSay(["That day's fully booked — try another?"], "date", { days: true }); return; }
      setSlots(available);
      agentSay(["Here's what's free (your local time) 👇"], "slot", { slots: true });
    } catch (e) {
      setTyping(false);
      agentSay(["Trouble reaching the calendar — try again?"], "date", { days: true });
    }
  };

  const pickSlot = (slot) => {
    pushUser(formatSlotLabel(slot.start)); setAnswers((a) => ({ ...a, slot })); setStep("waiting");
    agentSay(["Look right?"], "confirm", { confirm: true });
  };

  const confirmBooking = async () => {
    const a = answersRef.current;
    pushUser("Yes, book it! 🎉"); setStep("booking"); setTyping(true); scrollDown();
    try {
      const res = await fetch(`${getApiBaseUrl()}/user/demo-booking/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: a.name, email: a.email, company: a.company, interest: a.interest, slotStart: a.slot.start, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
      });
      const json = await res.json();
      setTyping(false);
      if (!res.ok) {
        if (res.status === 409) { agentSay(["That slot just got taken — pick another:"], "date", { days: true }); return; }
        throw new Error(json?.message || "Booking failed");
      }
      const meetingLink = json?.data?.meetingLink;
      // They've booked — permanently stop nudging, even on future visits.
      bookedRef.current = true;
      localStorage.setItem(BOOKED_KEY, "1");
      cancelPromo();
      agentSay([`Done! 🎉 You're booked for ${formatFull(a.slot.start)}.`, "Check your inbox for the calendar invite."], "done", { celebrate: true, meetingLink });
    } catch (e) {
      setTyping(false);
      agentSay(["Something went wrong — try again?"], "confirm", { confirm: true });
    }
  };

  const editBooking = () => { pushUser("Change the time"); setStep("waiting"); agentSay(["Pick another day:"], "date", { days: true }); };

  const showInput = ["name", "email", "company"].includes(step);
  const a = answers;

  return (
    <>
      {showPromo && (
        <div className="dw-promo-overlay" onClick={dismissPromo}>
          <div className="dw-promo-card" onClick={(e) => e.stopPropagation()}>
            <button className="dw-promo-close" onClick={dismissPromo} aria-label="Close">×</button>
            <div className="dw-promo-header">
              <span className="dw-promo-eyebrow">FREE 30-MINUTE VIDEO DEMO — NO SLIDES, NO PRESSURE</span>
              <h3>See IMERGEY <span>in action.</span></h3>
            </div>
            <div className="dw-promo-body">
              <p>Get a live, human-led walkthrough of every module and the exact day-to-day workflows you'll follow—while getting your questions answered in real time.</p>
              <ul>
                <li>✦ Tailored to your focus area</li>
                <li>✦ Calendar invite straight to your inbox</li>
                <li>✦ No commitment, ever</li>
              </ul>
              <div className="dw-promo-actions">
                <button className="dw-promo-primary" onClick={openPanel}>Chat with {AGENT_NAME} →</button>
                <a
                  className="dw-promo-secondary"
                  href="mailto:clientservices@imergey.com?subject=Question%20about%20IMERGEY"
                  onClick={dismissPromo}
                  title="Reach our team directly via email. We'll get back to you personally."
                >
                  ✉️ Drop us a note
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="dw-root">
          {mode === "panel" && (
            <div className={`dw-panel ${closingPanel ? "dw-panel-closing" : ""}`} role="dialog" aria-label="Book a demo">
              <div className="dw-panel-head">
                <div className="dw-panel-agent">
                  <div className="dw-panel-avatar">{AGENT_NAME[0]}<span className="dw-online" /></div>
                  <div>
                    <div className="dw-panel-name">{AGENT_NAME}</div>
                    <div className="dw-panel-role">IMERGEY Demo Concierge</div>
                  </div>
                </div>
                <button className="dw-panel-min" onClick={minimizePanel} aria-label="Minimize">–</button>
              </div>

              <div className="dw-stream" ref={streamRef}>
                {messages.map((m) => (
                  <div key={m.id} className={`dw-row dw-row-${m.from}`}>
                    <div className="dw-bubble-wrap">
                      <div className={`dw-bubble dw-bubble-${m.from} ${m.celebrate ? "dw-celebrate" : ""}`}>{m.text}</div>
                      {m.meetingLink && step === "done" && (
                        <a className="dw-teams-link" href={m.meetingLink} target="_blank" rel="noreferrer">🔗 Join Teams Meeting</a>
                      )}
                      {m.chips && step === "interest" && (
                        <div className="dw-chips">
                          {m.chips.map((c) => <button key={c} className="dw-chip" onClick={() => pickInterest(c)}>{c}</button>)}
                        </div>
                      )}
                      {m.days && step === "date" && (
                        <div className="dw-days">
                          {days.map((d) => (
                            <button key={d.value} className="dw-day" onClick={() => pickDate(d)}>
                              <span>{d.label}</span><b>{d.sub}</b>
                            </button>
                          ))}
                        </div>
                      )}
                      {m.slots && step === "slot" && slots && (
                        <div className="dw-slots">
                          {slots.map((s) => <button key={s.start} className="dw-slot" onClick={() => pickSlot(s)}>{formatSlotLabel(s.start)}</button>)}
                        </div>
                      )}
                      {m.confirm && step === "confirm" && a.slot && (
                        <div className="dw-card">
                          <div className="dw-card-when">{formatFull(a.slot.start)}</div>
                          <div className="dw-card-line">{a.name}{a.company ? ` · ${a.company}` : ""}</div>
                          <div className="dw-card-line">{a.email}</div>
                          <div className="dw-card-actions">
                            <button className="dw-confirm" onClick={confirmBooking}>Book it 🎉</button>
                            <button className="dw-edit" onClick={editBooking}>Change time</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {typing && (
                  <div className="dw-row dw-row-agent">
                    <div className="dw-bubble dw-bubble-agent dw-typing"><span /><span /><span /></div>
                  </div>
                )}
              </div>

              <div className={`dw-inputbar ${showInput ? "" : "dw-inputbar-off"}`}>
                {inputError && <div className="dw-error">{inputError}</div>}
                <div className="dw-inputrow">
                  <input
                    ref={inputRef}
                    value={inputValue}
                    disabled={!showInput}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitText()}
                    placeholder={step === "name" ? "Your name…" : step === "email" ? "you@company.com" : step === "company" ? "Company (optional)" : "Pick an option above"}
                  />
                  <button onClick={submitText} disabled={!showInput} aria-label="Send">➤</button>
                </div>
                <Link to="/book-demo" className="dw-expand-link">Open full booking page ↗</Link>
              </div>
            </div>
          )}

          {mode === "bubble" && (
            <button className="dw-bubble-btn" onClick={openPanel} aria-label="Book a demo">
              <span className="dw-bubble-ring" />
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
      </div>
    </>
  );
}
